/**
 * File Upload Enhancement for HubSpot Forms
 * Provides custom UI with progress tracking and multilingual support
 */

export function initFileUploads() {
	// Get current language and normalize it
	const htmlLang = document.documentElement.lang || 'sv';
	const currentLang = htmlLang.startsWith('nb') || htmlLang.startsWith('no') ? 'no' : 
					   htmlLang.startsWith('sv') ? 'sv' : 
					   htmlLang.startsWith('en') ? 'en' : 'sv';
	
	// Translations
	const translations = {
		'sv': {
			chooseFiles: 'Välj filer',
			addMoreFiles: 'Lägg till filer',
			uploading: 'Laddar upp...',
			complete: 'Klar'
		},
		'no': {
			chooseFiles: 'Velg filer',
			addMoreFiles: 'Legg til filer',
			uploading: 'Laster opp...',
			complete: 'Ferdig'
		},
		'en': {
			chooseFiles: 'Choose Files',
			addMoreFiles: 'Add More Files',
			uploading: 'Uploading...',
			complete: 'Complete'
		}
	};
	
	const t = translations[currentLang];
	
	const fileFields = document.querySelectorAll('.hs-fieldtype-file:not(.upload-enhanced)');
	
	fileFields.forEach(field => {
		const input = field.querySelector('input[type="file"]');
		const inputContainer = field.querySelector('.input');
		
		if (input && inputContainer) {
			// Mark as enhanced
			field.classList.add('upload-enhanced');
			
			// Hide original input
			input.style.display = 'none';
			
			// Create upload UI
			const uploadUI = document.createElement('div');
			uploadUI.innerHTML = `
				<button type="button" class="upload-button">${t.chooseFiles}</button>
				<div class="file-list"></div>
			`;
			inputContainer.appendChild(uploadUI);
			
			const button = uploadUI.querySelector('.upload-button');
			const fileList = uploadUI.querySelector('.file-list');
			
			// Handle button click
			button.addEventListener('click', () => {
				input.click();
			});
			
			// Handle file selection
			input.addEventListener('change', (e) => {
				if (e.target.files.length > 0) {
					// Combine existing files with new files
					const dt = new DataTransfer();
					
					// Add existing files first
					if (field.uploadedFiles) {
						field.uploadedFiles.forEach(file => dt.items.add(file));
					}
					
					// Add new files and track them for progress
					const newFiles = Array.from(e.target.files);
					newFiles.forEach(file => {
						dt.items.add(file);
						// Mark as new for progress tracking
						file._isNew = true;
					});
					
					// Store combined files
					field.uploadedFiles = Array.from(dt.files);
					input.files = dt.files;
					
					// Update button text
					button.textContent = t.addMoreFiles;
					
					// Update file list and start upload simulation for new files
					updateFileList();
					startUploadSimulation(newFiles);
				}
			});
			
			function updateFileList() {
				fileList.innerHTML = '';
				const files = field.uploadedFiles || Array.from(input.files);
				
				files.forEach((file, index) => {
					const fileItem = document.createElement('div');
					fileItem.className = 'file-item';
					fileItem.innerHTML = `
						<div class="file-info">
							<div class="file-details">
								<div class="file-name">${file.name}</div>
								<div class="file-size">${formatFileSize(file.size)}</div>
							</div>
							<button type="button" class="remove-file">×</button>
						</div>
						<div class="file-progress" ${file._isUploaded ? 'style="display:none"' : ''}>
							<div class="progress-bar"></div>
						</div>
						<div class="file-status ${file._isUploaded ? 'complete' : ''}">${file._isUploaded ? t.complete : (file._isNew ? t.uploading : t.complete)}</div>
					`;
					
					// Handle remove file
					fileItem.querySelector('.remove-file').addEventListener('click', (e) => {
						e.preventDefault();
						e.stopPropagation();
						removeFile(index);
					});
					
					fileList.appendChild(fileItem);
				});
			}
			
			function startUploadSimulation(newFiles) {
				newFiles.forEach((file) => {
					const globalIndex = field.uploadedFiles.indexOf(file);
					const fileItem = fileList.children[globalIndex];
					const progressContainer = fileItem.querySelector('.file-progress');
					const progressBar = fileItem.querySelector('.progress-bar');
					const statusElement = fileItem.querySelector('.file-status');
					
					let progress = 0;
					const interval = setInterval(() => {
						progress += Math.random() * 30; // Random progress increments
						if (progress >= 100) {
							progress = 100;
							progressBar.style.width = '100%';
							
							// Hide progress bar after a short delay
							setTimeout(() => {
								progressContainer.style.display = 'none';
								statusElement.textContent = t.complete;
								statusElement.className = 'file-status complete';
								file._isNew = false;
								file._isUploaded = true;
							}, 500);
							
							clearInterval(interval);
						} else {
							progressBar.style.width = progress + '%';
						}
					}, 200 + Math.random() * 300); // Random timing for realistic effect
				});
			}
			
			function removeFile(index) {
				
				// Remove from stored files array
				if (field.uploadedFiles) {
					field.uploadedFiles.splice(index, 1);
				}
				
				// Update input files without triggering change event
				const dt = new DataTransfer();
				if (field.uploadedFiles && field.uploadedFiles.length > 0) {
					field.uploadedFiles.forEach(file => dt.items.add(file));
					input.files = dt.files;
				} else {
					// Clear input if no files left
					input.value = '';
					delete field.uploadedFiles;
					button.textContent = t.chooseFiles;
				}
				
				// Update the display immediately
				updateFileList();
			}
			
			function formatFileSize(bytes) {
				if (bytes === 0) return '0 Bytes';
				const k = 1024;
				const sizes = ['Bytes', 'KB', 'MB', 'GB'];
				const i = Math.floor(Math.log(bytes) / Math.log(k));
				return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
			}
		}
	});
}